import { lazy, Suspense } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Activity, BadgeCheck, Blocks, ChartColumn, Clapperboard, Copyright, FolderInput, Handshake, HardDrive, KeyRound, Link2, Radio, Send, Server, ShieldCheck, Webhook } from 'lucide-react';
import { useMe } from '../../api/hooks';
import { ModelEditor } from './ModelEditor';
import { StorageAdmin } from './StorageAdmin';
import { ExportLocationsAdmin } from './ExportLocationsAdmin';
import { EditLocationsAdmin } from './EditLocationsAdmin';
import { AgentsAdmin } from './AgentsAdmin';
import { AccessAdmin } from './AccessAdmin';
import { SharedLinksAdmin } from './SharedLinksAdmin';
import { WatchFoldersAdmin } from './WatchFoldersAdmin';
import { SystemStatus } from './SystemStatus';
import { ApiKeysAdmin } from './ApiKeysAdmin';
import { WebhooksAdmin } from './WebhooksAdmin';
import { TranscodingAdmin } from './TranscodingAdmin';
import { LiveSourcesAdmin } from './LiveSourcesAdmin';
import { QualityControlAdmin } from './QualityControlAdmin';
import { PortalAdmin } from './portal/PortalAdmin';
// 1.8.0: Rights (docs/dev/rights-1.8.0-build.md).
import { RightsAdmin } from './RightsAdmin';
import { OverflowTabs } from '../../components/OverflowTabs';
// 1.6.0 (T, integrations/Integrations.tsx): Integrations → AI assistants (MCP).
import { IntegrationsAdmin, integrationsTabVisible } from './integrations/Integrations';

// Recharts is only downloaded when Statistics opens.
const Statistics = lazy(() => import('./Statistics'));

export function Admin() {
  const me = useMe().data!;
  const isAdmin = me.permissions.includes('admin');
  const canStorage = me.permissions.includes('storage.manage');
  const canLive = canStorage || me.permissions.includes('asset.upload');
  // The media portal (1.3.0): its own section, for portal.manage.
  const canPortal = me.permissions.includes('portal.manage');
  // The tab row scrolls sideways when it does not fit: the current tab is kept in view.
  const { pathname } = useLocation();
  const tabs = [
    isAdmin && { to: 'status', label: 'System status', icon: <Activity size={15} /> },
    isAdmin && { to: 'statistics', label: 'Statistics', icon: <ChartColumn size={15} /> },
    isAdmin && { to: 'model', label: 'Metadata model', icon: <Blocks size={15} /> },
    canStorage && { to: 'storage', label: 'Storage', icon: <HardDrive size={15} /> },
    canStorage && { to: 'agents', label: 'Storage agents', icon: <Server size={15} /> },
    canStorage && { to: 'watch-folders', label: 'Watch folders', icon: <FolderInput size={15} /> },
    canStorage && { to: 'exports', label: 'Export locations', icon: <Send size={15} /> },
    canStorage && { to: 'edit-locations', label: 'Edit locations', icon: <Clapperboard size={15} /> },
    canLive && { to: 'live', label: 'Live sources', icon: <Radio size={15} /> },
    isAdmin && { to: 'transcoding', label: 'Transcoding', icon: <Clapperboard size={15} /> },
    isAdmin && { to: 'quality-control', label: 'Quality control', icon: <BadgeCheck size={15} /> },
    // 1.8.0: rights management (off until switched on here).
    isAdmin && { to: 'rights', label: 'Rights', icon: <Copyright size={15} /> },
    { to: 'access', label: 'Access', icon: <ShieldCheck size={15} /> },
    // 1.6.3: every share link with its view history.
    isAdmin && { to: 'shared-links', label: 'Shared links', icon: <Link2 size={15} /> },
    canPortal && { to: 'portal', label: 'Media portal', icon: <Handshake size={15} /> },
    isAdmin && { to: 'webhooks', label: 'Webhooks', icon: <Webhook size={15} /> },
    isAdmin && { to: 'api-keys', label: 'API keys', icon: <KeyRound size={15} /> },
    integrationsTabVisible(me) && { to: 'integrations', label: 'Integrations', icon: <Blocks size={15} /> },
  ].filter(Boolean) as Array<{ to: string; label: string; icon: React.ReactNode }>;
  return (
    <>
      <header className="topbar">
        <h1 className="admin-title"><span className="admin-title-main">Administration</span> <span className="crumb" title={me.workspace.name}>· {me.workspace.name}</span></h1>
        <OverflowTabs as="nav" className="admin-tabs" wrapClassName="admin-tabs-wrap" label="Administration sections" activeKey={pathname} testId="admin-tabs">
          {tabs.map((t) => (
            // Absolute paths: inside the /admin/* splat route, relative links resolve against the
            // current tab's URL (React Router 7), which appended segments and looped via the redirect.
            <NavLink key={t.to} to={`/admin/${t.to}`} className={({ isActive }) => `admin-tab ${isActive ? 'active' : ''}`} data-testid={`admin-tab-${t.to}`}>{t.icon}{t.label}</NavLink>
          ))}
        </OverflowTabs>
      </header>
      <Routes>
        {isAdmin && <Route path="status" element={<SystemStatus />} />}
        {isAdmin && <Route path="statistics" element={<Suspense fallback={<div className="page"><p className="muted">Loading…</p></div>}><Statistics /></Suspense>} />}
        {isAdmin && <Route path="model" element={<ModelEditor />} />}
        {/* Asset classes and Timecode layers are views of the Metadata model page (0.91.0). */}
        {isAdmin && <Route path="classes" element={<Navigate to="/admin/model?view=classes" replace />} />}
        {isAdmin && <Route path="strata" element={<Navigate to="/admin/model?view=layers" replace />} />}
        {canStorage && <Route path="storage" element={<StorageAdmin />} />}
        {canStorage && <Route path="watch-folders" element={<WatchFoldersAdmin />} />}
        {canStorage && <Route path="exports" element={<ExportLocationsAdmin />} />}
        {canStorage && <Route path="edit-locations" element={<EditLocationsAdmin />} />}
        {canLive && <Route path="live" element={<LiveSourcesAdmin />} />}
        {canStorage && <Route path="agents/*" element={<AgentsAdmin />} />}
        {isAdmin && <Route path="transcoding" element={<TranscodingAdmin />} />}
        {isAdmin && <Route path="quality-control" element={<QualityControlAdmin />} />}
        {isAdmin && <Route path="rights" element={<RightsAdmin />} />}
        <Route path="access" element={<AccessAdmin />} />
        {isAdmin && <Route path="shared-links" element={<SharedLinksAdmin />} />}
        {canPortal && <Route path="portal" element={<PortalAdmin />} />}
        {isAdmin && <Route path="webhooks" element={<WebhooksAdmin />} />}
        {isAdmin && <Route path="api-keys" element={<ApiKeysAdmin />} />}
        {integrationsTabVisible(me) && <Route path="integrations" element={<IntegrationsAdmin />} />}
        <Route path="*" element={<Navigate to={`/admin/${tabs[0]!.to}`} replace />} />
      </Routes>
    </>
  );
}
